import { dateRangeValidator, DATE_RANGE_MESSAGE } from '../../../core/utils/date-range.validator';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import * as ClientConstants from '../../../core/constants/client.constants';
import * as CountryConstants from '../../../core/constants/country.constants';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import { CommonModule } from '@angular/common';
import { CodesService } from '../../../core/services/codes/codes.service';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';

@Component({
  selector: 'app-addresses-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    CheckboxModule,
    CalendarModule,
    AccordionModule,
    ClientsTableComponent,
    CommonModule,
    InputTextareaModule,
    AutoAuditDirective,
  ],
  templateUrl: './addresses-form.component.html',
  styleUrl: './addresses-form.component.scss',
})
export class AddressesFormComponent implements OnChanges {
  public clientConstants = ClientConstants;
  public countryConstants = CountryConstants;

  fieldAuditData: LatestPerFieldMap = {};

  configAddressesTable: any = {
    headers: [
      { name: 'Tipo', key: 'type' },
      { name: 'Dirección', key: 'formattedAddress' },
      { name: 'Fecha de alta', key: 'startDate' },
      { name: 'Fecha de baja', key: 'endDate' },
      { name: 'Referencia catastral', key: 'cadastralReference' },
      { name: 'Observaciones', key: 'observations' },
      { name: '', key: 'duplicate' },
      { name: '', key: 'delete' },
    ],
    data: [],
    isAddresses: true,
  };

  addressesForm: FormGroup;
  addressForm: FormGroup;

  isAddressModalVisible = false;
  isDeleteAddressModalVisible = false;
  isDuplicateAddressModalVisible = false;
  editingAddressIndex: number | undefined = undefined;
  deleteAddressIndex: number;
  duplicateAddressIndex: number;
  selectedDuplicateType: string = '';

  selectedTypeFilter: string = '';
  allAddressesData: any[] = [];

  iaeCodes: any[] = [];
  cnaeCodes: any[] = [];

  @Input() clientId: any;
  @Input() snapshotData: any[] | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private codesService: CodesService,
    private auditService: AuditService,
  ) {
    this.addressesForm = this.fb.group({
      addresses: this.fb.array([]),
    });

    this.addressForm = this.fb.group({
      id: [],
      type: ['', Validators.required],
      startDate: [null, Validators.required],
      acronym: [''],
      street: [''],
      number: [''],
      stairs: [''],
      floor: [''],
      door: [''],
      town: [''],
      postalCode: [''],
      province: [''],
      codeCountry: [''],
      country: [''],
      countrySelector: [''], // Combined selector for UI
      cadastralReference: [''],
      endDate: [null],
      client: [null],
      observations: [''],
    });

    this.addressesForm.valueChanges.subscribe(() => {
      if (this.addressesForm.dirty) {
        this.formIsDirty.emit(true);
      }
    });

    this.addressForm.addValidators(dateRangeValidator());
    // When combined country selector changes, update iso2 and iso3 fields
    this.addressForm.get('countrySelector')?.valueChanges.subscribe((iso3) => {
      if (iso3) {
        const selectedCountry = CountryConstants.COUNTRIES_RAW.find(
          (c) => c.iso3 === iso3,
        );
        if (selectedCountry) {
          this.addressForm.patchValue(
            {
              codeCountry: selectedCountry.iso2,
              country: selectedCountry.iso3,
            },
            { emitEvent: false },
          );
        }
      }
    });
  }

  async ngOnInit() {
    if (!this.snapshotData) {
      await this.getData();
    }
    this.iaeCodes = await this.codesService.getIaeCodes();
    this.cnaeCodes = await this.codesService.getCnaeCodes();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.configAddressesTable = {
          ...this.configAddressesTable,
          data: this.formatAddressData(this.snapshotData),
        };
      } else if (!changes['snapshotData'].firstChange) {
        void this.getData();
      }
    }
  }

  formatAddressData(addressesData: any[]): any[] {
    return addressesData.map((address) => ({
      ...address,
      formattedAddress: this.formatAddress(address),
    }));
  }

  formatAddress(address: any): string {
    const acronym = address.acronym?.toString().trim();
    const street = address.street?.toString().trim();
    const streetPart =
      acronym && street ? `${acronym} ${street}` : acronym || street;

    const parts = [
      streetPart,
      address.number,
      address.stairs,
      address.floor,
      address.door,
      address.town,
      address.postalCode,
      address.province,
      address.country,
    ].filter((part) => part && part.toString().trim() !== '');

    return parts.join(', ');
  }

  async getData() {
    if (this.clientId) {
      // Get addresses data
      const addressesData = await this.clientsService.getClientAddresses(
        this.clientId,
      );
      this.addresses.clear();
      addressesData.forEach((address) => {
        address.startDate = parseDateOnlyToLocal(address.startDate);
        address.endDate = parseDateOnlyToLocal(address.endDate);
        this.addresses.push(
          this.fb.group({
            type: [address.type, Validators.required],
            startDate: [address.startDate, Validators.required],
            acronym: [address.acronym],
            street: [address.street],
            number: [address.number],
            stairs: [address.stairs],
            floor: [address.floor],
            door: [address.door],
            town: [address.town],
            postalCode: [address.postalCode],
            province: [address.province],
            codeCountry: [address.codeCountry],
            country: [address.country],
            cadastralReference: [address.cadastralReference],
            endDate: [address.endDate],
            id: [address.id],
            client: [address.client],
            observations: [address.observations ?? ''],
          }),
        );
      });

      this.allAddressesData = this.formatAddressData(addressesData);
      this.applyTypeFilter();

      try {
        this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_address');
      } catch {
        this.fieldAuditData = {};
      }
    }
  }

  applyTypeFilter() {
    if (!this.selectedTypeFilter) {
      // Show all addresses if no filter is selected
      this.configAddressesTable.data = this.allAddressesData;
    } else {
      // Filter addresses by selected type
      this.configAddressesTable.data = this.allAddressesData.filter(
        (address) => address.type === this.selectedTypeFilter,
      );
    }
  }

  onTypeFilterChange() {
    this.applyTypeFilter();
  }

  get activeAddressesTableConfig(): any {
    if (!this.snapshotData) return this.configAddressesTable;
    return {
      ...this.configAddressesTable,
      headers: this.configAddressesTable.headers.filter(
        (h: any) => h.key !== 'delete' && h.key !== 'duplicate',
      ),
    };
  }

  get addresses(): FormArray {
    return this.addressesForm.get('addresses') as FormArray;
  }

  addAddress() {
    const address = this.fb.group({
      type: ['', Validators.required],
      startDate: [, Validators.required],
      acronym: [''],
      street: [''],
      number: [''],
      stairs: [''],
      floor: [''],
      door: [''],
      town: [''],
      postalCode: [''],
      province: [''],
      codeCountry: [''],
      country: [''],
      cadastralReference: [''],
      endDate: [],
      id: [],
      client: [],
      observations: [''],
    });

    this.addresses.push(address);
  }

  private toAddressPayload(address: any) {
    return {
      ...address,
      startDate: toDateOnlyPayload(address.startDate),
      endDate: toDateOnlyPayload(address.endDate),
    };
  }

  async save() {
    // Save addresses
    for (let i = 0; i < this.addresses.length; i++) {
      if (this.addresses.at(i).dirty) {
        const address = this.addresses.at(i).value;
        address.client = Number(this.clientId);
        try {
          await this.clientsService.saveClientAddress(this.toAddressPayload(address));
        } catch (e) {
          console.error(
            '[Addresses] Failed saving address (bulk save). Payload:',
            address,
            'Error:',
            e,
          );
          throw e;
        }
      }
    }
    await this.getData();
  }

  /** Guarda de envío: un doble clic crearía duplicados. */
  isSubmitting = false;
  /** Último error de guardado o borrado de un modal, para mostrarlo al usuario. */
  submitError = '';

  async saveAddress() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSaveAddress();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSaveAddress() {
    if (this.addressForm.invalid) return;
    const { countrySelector, ...value } = this.addressForm.value;
    value.client = Number(this.clientId);

    try {
      if (this.editingAddressIndex !== undefined) {
        const contactGroup = this.addresses.at(this.editingAddressIndex);
        contactGroup.patchValue(value);
        await this.clientsService.saveClientAddress(
          this.toAddressPayload({ ...contactGroup.value, id: value.id }),
        );
      } else {
        await this.clientsService.saveClientAddress(this.toAddressPayload(value));
      }
    } catch (e) {
      console.error(
        '[Addresses] Failed saving address (modal). ClientId:',
        this.clientId,
        'Payload:',
        value,
        'Error:',
        e,
      );
      throw e; // lo recoge el guardado protegido y lo muestra en el modal
    }
    await this.getData();
    this.closeAddressModal();
  }

  /**
   * La tabla emite el índice de la lista filtrada por tipo; el FormArray tiene todos los domicilios.
   * Se traduce por id para no editar, duplicar o borrar otra fila.
   */
  private formIndexFromTable(tableIndex: number): number {
    const row = this.configAddressesTable.data[tableIndex];
    return this.addresses.controls.findIndex((c) => c.value.id === row?.id);
  }

  openAddressModal(index?: number) {
    this.submitError = '';
    if (index !== undefined && !this.snapshotData) {
      index = this.formIndexFromTable(index);
      if (index === -1) return;
    }
    this.editingAddressIndex = index;
    if (index !== undefined) {
      const address = this.snapshotData
        ? this.configAddressesTable.data[index]
        : this.addresses.at(index).value;
      // Find iso3 from existing data to set countrySelector
      let countrySelector = '';
      if (address.country) {
        // If country is already iso3, use it
        const countryData = CountryConstants.COUNTRIES_RAW.find(
          (c) => c.iso3 === address.country,
        );
        if (countryData) {
          countrySelector = countryData.iso3;
        } else {
          // If country is name, find by name
          const countryByName = CountryConstants.COUNTRIES_RAW.find(
            (c) => c.nameES === address.country,
          );
          if (countryByName) {
            countrySelector = countryByName.iso3;
          }
        }
      } else if (address.codeCountry) {
        // If only codeCountry exists, find by iso2
        const countryByCode = CountryConstants.COUNTRIES_RAW.find(
          (c) => c.iso2 === address.codeCountry,
        );
        if (countryByCode) {
          countrySelector = countryByCode.iso3;
        }
      }
      this.addressForm.patchValue({
        ...address,
        countrySelector,
      });
    } else {
      this.addressForm.reset();
    }
    this.isAddressModalVisible = true;
    if (this.snapshotData) {
      this.addressForm.disable();
    }
  }

  closeAddressModal() {
    this.submitError = '';
    this.isAddressModalVisible = false;
    this.editingAddressIndex = undefined;
    this.addressForm.reset();
  }

  openDuplicateAddressModal(index: number) {
    this.submitError = '';
    index = this.formIndexFromTable(index);
    if (index === -1) return;
    this.duplicateAddressIndex = index;
    const address = this.addresses.at(index).value;
    this.selectedDuplicateType = address?.type ?? '';
    this.isDuplicateAddressModalVisible = true;
  }

  closeDuplicateAddressModal() {
    this.submitError = '';
    this.isDuplicateAddressModalVisible = false;
    this.duplicateAddressIndex = undefined;
    this.selectedDuplicateType = '';
  }

  async confirmDuplicateAddress() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doConfirmDuplicateAddress();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doConfirmDuplicateAddress() {
    if (!this.selectedDuplicateType) {
      return;
    }
    const address = this.addresses.at(this.duplicateAddressIndex).value;
    const payload = {
      ...address,
      id: null,
      type: this.selectedDuplicateType,
      client: Number(this.clientId),
    };

    try {
      await this.clientsService.saveClientAddress(payload);
    } catch (e) {
      console.error(
        'Error duplicando el domiciulio. Cliente:',
        this.clientId,
        'Datos:',
        payload,
        'Error:',
        e,
      );
      throw e; // lo recoge el guardado protegido y lo muestra en el modal
    }

    await this.getData();
    this.closeDuplicateAddressModal();
  }

  async confirmDeleteAddress() {
    this.submitError = '';
    try {
      await this.doConfirmDeleteAddress();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    }
  }

  private async doConfirmDeleteAddress() {
    const address = this.addresses.at(this.deleteAddressIndex).value;
    if (address.id) {
      await this.clientsService.deleteClientAddress(address.id);
      await this.getData();
    }
    this.deleteAddressIndex = undefined;
    this.isDeleteAddressModalVisible = false;
  }

  async openDeleteAddressModal(index: number) {
    this.submitError = '';
    index = this.formIndexFromTable(index);
    if (index === -1) return;
    this.deleteAddressIndex = index;
    this.isDeleteAddressModalVisible = true;
  }

  closeDeleteAddressModal() {
    this.submitError = '';
    this.deleteAddressIndex = undefined;
    this.isDeleteAddressModalVisible = false;
  }
}
